'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const BRAND = '#3757FF';
const liveStats = [
    { value: '12k+', label: 'Active learners', color: 'text-primary' },
    { value: '500+', label: 'Live instructors', color: 'text-orange' },
    { value: '95%', label: 'Satisfaction rate', color: 'text-green' }];


export default function StudioHero() {
    const blob1Ref = useRef<HTMLDivElement>(null);
    const blob2Ref = useRef<HTMLDivElement>(null);

    const [mounted, setMounted] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => { setMounted(true); }, []);

    useEffect(() => {
        if (!mounted) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        let animId: number;
        let t = 0;
        const resize = () => {
            canvas.width = canvas.offsetWidth * window.devicePixelRatio;
            canvas.height = canvas.offsetHeight * window.devicePixelRatio;
            ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        };
        resize();
        window.addEventListener('resize', resize);
        const draw = () => {
            const w = canvas.offsetWidth;
            const h = canvas.offsetHeight;
            ctx.clearRect(0, 0, w, h);
            t += 0.003;
            const orbs = [
                { x: w * 0.8 + Math.sin(t) * 80, y: h * 0.3 + Math.cos(t * 0.7) * 60, r: 400, color: 'rgba(55,87,255,0.07)' },
                { x: w * 0.1 + Math.cos(t * 0.9) * 60, y: h * 0.6 + Math.sin(t * 1.1) * 50, r: 350, color: 'rgba(55,87,255,0.05)' },
                { x: w * 0.5 + Math.sin(t * 1.3) * 100, y: h * 0.9 + Math.cos(t * 0.5) * 40, r: 280, color: 'rgba(100,130,255,0.06)' },
            ];
            orbs.forEach(orb => {
                const grad = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.r);
                grad.addColorStop(0, orb.color);
                grad.addColorStop(1, 'transparent');
                ctx.fillStyle = grad;
                ctx.beginPath();
                ctx.arc(orb.x, orb.y, orb.r, 0, Math.PI * 2);
                ctx.fill();
            });
            animId = requestAnimationFrame(draw);
        };
        draw();
        return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
    }, [mounted]);

    return (
        <section className="relative min-h-[90vh] flex flex-col justify-center overflow-hidden bg-background pt-16">
            {/* Atmospheric background */}
            <div className="absolute inset-0 grid-bg pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div
                    ref={blob1Ref}
                    className="absolute top-[-10%] left-[-5%] w-[700px] h-[700px] blob-primary animate-blob will-change-transform" />

                <div
                    ref={blob2Ref}
                    className="absolute bottom-[-15%] right-[-10%] w-[600px] h-[600px] blob-accent animate-blob-delay will-change-transform" />

                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] blob-orange opacity-40" />
                <div className="noise-overlay absolute inset-0" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 md:py-20">
                    <div className="flex flex-col gap-6 md:gap-8">
                        {/* Eyebrow */}
                        <div className="animate-hero-1">
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-blue text-primary text-sm font-semibold">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                                </span>
                                Wisly Studio — For instructors
                            </span>
                        </div>

                        {/* Headline */}
                        <div className="animate-hero-2">
                            <h1 className="text-hero font-extrabold text-foreground">
                                All you need{' '}
                                <span className="text-gradient-blue">to teach and manage.</span>
                            </h1>
                        </div>

                        {/* Supporting copy */}
                        <div className="animate-hero-3">
                            <p className="text-xl md:text-2xl text-muted-foreground font-body leading-relaxed max-w-2xl">
                                Wisly Studio is the operational workspace for instructors{' '}
                                <span className="text-foreground font-semibold">
                                    — built to handle every dimension of running an educational operation.
                                </span>
                            </p>
                        </div>

                        {/* CTAs */}
                        <div className="animate-hero-4 flex flex-col sm:flex-row gap-3 mb-10">
                            <Link
                                href="#how-it-works"
                                className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-primary text-primary-foreground rounded-full text-base font-semibold hover:bg-primary/90 shadow-blue hover:shadow-blue-lg hover:-translate-y-0.5 transition-all duration-300">

                                See how it works
                                <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                    <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Link>
                            <Link
                                href="#categories"
                                className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-card text-foreground border border-border rounded-full text-base font-semibold hover:border-primary/30 hover:bg-primary/5 hover:-translate-y-0.5 transition-all duration-300 shadow-card">

                                Browse live sessions
                            </Link>
                        </div>

                        {/* Stats row */}
                        <div className="animate-hero-5 flex flex-wrap gap-6">
                            {liveStats.map((stat) =>
                                <div key={stat.label} className="flex items-center gap-2">
                                    <span className={`text-2xl font-extrabold ${stat.color}`}>{stat.value}</span>
                                    <span className="text-muted-foreground text-sm font-body">{stat.label}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: Studio workspace mockup */}
                    <div className="relative h-[460px] md:h-[650px] animate-hero-5">
                        <div
                            className="rounded-3xl p-6 relative overflow-hidden"
                            style={{ background: 'white', boxShadow: '0 24px 80px rgba(55,87,255,0.12)', border: '1px solid rgba(55,87,255,0.1)' }}
                        >
                            {/* Header bar */}
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <p className="text-xs font-700 uppercase tracking-widest mb-1" style={{ color: '#999', letterSpacing: '0.12em' }}>Studio Dashboard</p>
                                    <p className="text-lg font-800" style={{ color: '#0D0D0D' }}>Good morning, Mr. Osei 👋</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full" style={{ background: '#10B981' }} />
                                    <span className="text-xs font-600" style={{ color: '#10B981' }}>Live session active</span>
                                </div>
                            </div>

                            {/* Stats row */}
                            <div className="grid grid-cols-4 gap-3 mb-5">
                                {[
                                    { label: 'Students', value: '148' },
                                    { label: 'Classes Today', value: '4' },
                                    { label: 'Pending Reviews', value: '12' },
                                    { label: 'Avg Score', value: '81%' },
                                ].map(stat => (
                                    <div key={stat.label} className="rounded-xl p-3 text-center" style={{ background: '#F7F8FF', border: '1px solid rgba(55,87,255,0.08)' }}>
                                        <p className="text-lg font-800 mb-0.5" style={{ color: BRAND }}>{stat.value}</p>
                                        <p className="text-xs font-600" style={{ color: '#888' }}>{stat.label}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Today's schedule */}
                            <div className="rounded-2xl p-4 mb-4" style={{ background: '#F7F8FF', border: '1px solid rgba(55,87,255,0.1)' }}>
                                <p className="text-xs font-700 uppercase tracking-widest mb-3" style={{ color: '#999', letterSpacing: '0.12em' }}>Today's Schedule</p>
                                <div className="flex flex-col gap-2.5">
                                    {[
                                        { time: '09:00', subject: 'Algebra — Grade 10', status: 'done', students: 24 },
                                        { time: '11:00', subject: 'Calculus — Grade 12', status: 'live', students: 18 },
                                        { time: '14:00', subject: 'Statistics — Grade 11', status: 'upcoming', students: 22 },
                                    ].map(cls => (
                                        <div key={cls.time} className="flex items-center gap-3">
                                            <span className="text-xs font-700 w-12 flex-shrink-0" style={{ color: '#888' }}>{cls.time}</span>
                                            <div className="flex-1">
                                                <p className="text-xs font-700" style={{ color: '#0D0D0D' }}>{cls.subject}</p>
                                                <p className="text-xs font-500" style={{ color: '#AAA' }}>{cls.students} students</p>
                                            </div>
                                            <span
                                                className="text-xs font-700 px-2.5 py-1 rounded-full"
                                                style={{
                                                    background: cls.status === 'live' ? BRAND : cls.status === 'done' ? 'rgba(16,185,129,0.1)' : 'rgba(55,87,255,0.08)',
                                                    color: cls.status === 'live' ? 'white' : cls.status === 'done' ? '#10B981' : '#888',
                                                }}
                                            >
                                                {cls.status === 'live' ? '● Live' : cls.status === 'done' ? 'Done' : 'Upcoming'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Quick actions */}
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { label: 'New Assessment', icon: '📋' },
                                    { label: 'Add Student', icon: '👤' },
                                    { label: 'View Reports', icon: '📊' },
                                ].map(action => (
                                    <div
                                        key={action.label}
                                        className="rounded-xl p-3 text-center cursor-pointer transition-all duration-200"
                                        style={{ background: '#F7F8FF', border: '1px solid rgba(55,87,255,0.08)' }}
                                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = BRAND; (e.currentTarget as HTMLElement).style.background = 'rgba(55,87,255,0.05)'; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.08)'; (e.currentTarget as HTMLElement).style.background = '#F7F8FF'; }}
                                    >
                                        <p className="text-base mb-1">{action.icon}</p>
                                        <p className="text-xs font-600" style={{ color: '#555' }}>{action.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Floating badge */}
                        <div
                            className="absolute -right-4 rounded-2xl px-5 py-3 flex items-center gap-3"
                            style={{ background: 'white', boxShadow: '0 8px 32px rgba(55,87,255,0.15)', border: '1px solid rgba(55,87,255,0.1)' }}
                        >
                            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(55,87,255,0.1)' }}>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M2 10l3-4 2.5 2.5 3-4.5 2.5 3" stroke={BRAND} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs font-800" style={{ color: '#0D0D0D' }}>Student performance</p>
                                <p className="text-xs font-600" style={{ color: '#888' }}>+11% this month</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section >
    );
}
